import { Avatar } from '@gx-design/avatar';
import { Button } from '@gx-design/button';
import { Icon, IconProps } from '@gx-design/icon';
import { TextareaProps } from '@gx-design/textarea';
import { trans } from '@pepita-i18n/babelfish';
import clsx from 'clsx';
import React, {
    FC,
    Fragment,
    PropsWithChildren,
    createElement,
    forwardRef,
    useRef,
} from 'react';

import { Skeleton } from 'gtx-react/components/Skeleton';
import { formatDate } from 'lib/datefns';
import { getFormattedDate } from 'lib/smartDateFormatter';

type BubbleProps = PropsWithChildren<{
    classNameSuffix?: string;
    icon?: IconProps['name'];
    url?: string;
    urlClassName?: string;
}>;
const Bubble: FC<BubbleProps> = (props) => {
    const wrapper = props.url ? 'a' : Fragment;
    const wrapperProps = props.url
        ? ({
              href: props.url,
              target: '_blank',
              className: props.urlClassName,
          } as HTMLAnchorElement)
        : {};
    const children = [
        props.icon && (
            <span className="crm-chat__bubbleIcon" key={props.icon}>
                <Icon name={props.icon} />
            </span>
        ),
        props.children,
    ];

    return (
        <div
            className={clsx(
                'crm-chat__bubble',
                props.classNameSuffix &&
                    `crm-chat__bubble--${props.classNameSuffix}`
            )}
        >
            {createElement(wrapper, wrapperProps, children)}
        </div>
    );
};

type SeparatorProps = { date?: string };
const Separator = ({ date }: SeparatorProps) => (
    <div className="crm-chat__separator">
        {date && (
            <time dateTime={date} className="crm-chat__date gx-body-tiny">
                {getFormattedDate(new Date(date), {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                })}
            </time>
        )}
    </div>
);

type GroupProps = PropsWithChildren<{
    time?: string;
    self?: boolean;
    avatarUrl?: string;
    avatarAlt?: string;
    status?: string;
}>;
const Group = (props: GroupProps) => (
    <div
        className={clsx('crm-chat__bubblesGroup', {
            'crm-chat__bubblesGroup--received': !props.self,
            'crm-chat__bubblesGroup--sent': props.self,
        })}
    >
        {!props.self && (
            <Avatar
                wrapperClassName="crm-chat__avatar"
                alt={props.avatarAlt}
                src={props.avatarUrl}
            />
        )}
        <div className="crm-chat__bubbles">{props.children}</div>
        <div className="crm-chat__time gx-body-tiny">
            <span>
                {props.self && props.status ? `${props.status} | ` : null}
                {props.time && formatDate(new Date(props.time), 'H:mm')}
            </span>
        </div>
    </div>
);

type GroupSkeletonProps = { self?: boolean; single?: boolean };
const GroupSkeleton = ({ self, single }: GroupSkeletonProps) => (
    <div
        className={clsx('crm-chat__bubblesGroup', {
            'crm-chat__bubblesGroup--received': !self,
            'crm-chat__bubblesGroup--sent': self,
        })}
    >
        {!self && (
            <Skeleton className="crm-chat__avatar" width={32} height={32} />
        )}
        {!self ? (
            <div className="crm-chat__bubbles">
                <Skeleton
                    className="crm-chat__bubble"
                    width={500}
                    height={40}
                />
            </div>
        ) : (
            <div className="crm-chat__bubbles">
                <Skeleton
                    className="crm-chat__bubble"
                    width={328}
                    height={40}
                />
                {!single && (
                    <Skeleton
                        className="crm-chat__bubble"
                        width={130}
                        height={40}
                    />
                )}
            </div>
        )}
    </div>
);

export type ChatAttachment = {
    file: File;
    previewUrl?: string;
};

const isImageFile = (file: File) => file.type.startsWith('image/');

type ChatInputProps = {
    onSendMessage?: () => void;
    attachments?: ChatAttachment[];
    onAddAttachments?: (attachments: ChatAttachment[]) => void;
    onRemoveAttachment?: (index: number) => void;
    acceptedFileTypes?: string;
    isSendingDisabled?: boolean;
} & Omit<TextareaProps, 'label'>;

const ChatInput: FC<ChatInputProps> = ({
    onSendMessage,
    attachments = [],
    onAddAttachments,
    onRemoveAttachment,
    acceptedFileTypes = 'image/*,.pdf',
    isSendingDisabled = false,
    ...props
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) {
            return;
        }

        const newFiles: File[] = Array.from(files);

        const newAttachments: ChatAttachment[] = newFiles.map((file) => ({
            file,
            previewUrl: isImageFile(file)
                ? URL.createObjectURL(file)
                : undefined,
        }));

        onAddAttachments?.(newAttachments);

        // Reset input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="crm-chat-input">
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes}
                multiple
                onChange={handleFileChange}
                className="hidden"
            />
            <Button
                iconOnly
                variant="ghost"
                size="small"
                className="crm-chat-input__button"
                onClick={handleFileSelect}
                disabled={props.disabled}
            >
                <Icon name="plus" />
                <span className="gx-sr-only">
                    {trans('btn.label.add_attachment')}
                </span>
            </Button>
            <div className="crm-chat-input__composer">
                {attachments.length > 0 && (
                    <div className="crm-chat-input__attachments">
                        {attachments.map((attachment, index) =>
                            isImageFile(attachment.file) ? (
                                <div
                                    key={index}
                                    className="crm-chat-input__attachment crm-chat-input__attachment--image"
                                >
                                    <img
                                        src={attachment.previewUrl}
                                        alt={attachment.file.name}
                                    />
                                    <button
                                        type="button"
                                        className="crm-chat-input__attachmentClose"
                                        onClick={() =>
                                            onRemoveAttachment?.(index)
                                        }
                                    >
                                        <Icon name="cross" />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    key={index}
                                    className="crm-chat-input__attachment crm-chat-input__attachment--file"
                                >
                                    <span className="crm-chat-input__attachmentType">
                                        <Icon name="clip" />
                                    </span>
                                    <span className="crm-chat-input__attachmentName">
                                        {attachment.file.name}
                                    </span>
                                    <button
                                        type="button"
                                        className="crm-chat-input__attachmentClose"
                                        onClick={() =>
                                            onRemoveAttachment?.(index)
                                        }
                                    >
                                        <Icon name="cross" />
                                    </button>
                                </div>
                            )
                        )}
                    </div>
                )}
                <div
                    className="crm-chat-input__textareaWrapper"
                    data-replicated-value={props.value}
                >
                    <textarea
                        className="crm-chat-input__textarea"
                        name="text"
                        id="text"
                        rows={1}
                        placeholder={trans('label.messaging.write_a_message')}
                        {...props}
                    />
                </div>
            </div>
            <Button
                iconOnly
                variant="ghost"
                size="small"
                className="crm-chat-input__button"
                onClick={onSendMessage}
                disabled={isSendingDisabled}
                type="button"
            >
                <Icon name="send" />
                <span className="gx-sr-only">{trans('btn.label.send')}</span>
            </Button>
        </div>
    );
};
type ChatProps = PropsWithChildren<{
    initialContent?: React.ReactNode;
    inputComponent?: React.ReactNode;
}>;

type ChatComponent = React.ForwardRefExoticComponent<
    ChatProps & React.RefAttributes<HTMLDivElement>
> & {
    Group: typeof Group;
    Separator: typeof Separator;
    GroupSkeleton: typeof GroupSkeleton;
    Input: typeof ChatInput;
};

export const Chat = forwardRef<HTMLDivElement, ChatProps>((props, ref) => (
    <div className="crm-chat-wrapper">
        <div className="crm-chat" ref={ref}>
            {props.initialContent}
            <div className="crm-chat__content">{props.children}</div>
        </div>
        {props?.inputComponent}
    </div>
)) as ChatComponent;

Group.Bubble = Bubble;
Chat.displayName = 'Chat';
Chat.Separator = Separator;
Chat.Group = Group;
Chat.GroupSkeleton = GroupSkeleton;
Chat.Input = ChatInput;
